// src/pages/KOTManagementPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { API_ENDPOINTS } from "../config/constants";
import { formatStoredMoney } from "../utils/currency";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaChevronDown,
  FaChevronUp,
  FaSyncAlt,
  FaCheckCircle,
  FaBox,
} from "react-icons/fa";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#fff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  green: "#16a34a",
  amber: "#d97706",
  red: "#dc2626",
};

const STATUS = {
  pending: "pending",
  prepared: "prepared",
  delivered: "delivered",
};

const LIST_KOT_API = API_ENDPOINTS.LIST_KOTS;
const ACTION_KOT_API = API_ENDPOINTS.UPDATE_KOT_STATUS;
const DELETE_KOT_API = API_ENDPOINTS.DELETE_KOT_ITEM;

const fmtINR = (n) => formatStoredMoney(n, { fractionDigits: 0 });

const parseNumeric = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  const raw = String(value).trim();
  if (!raw) return NaN;
  const direct = Number(raw);
  if (Number.isFinite(direct)) return direct;
  const cleaned = raw.replace(/[^0-9.-]/g, "");
  if (!cleaned) return NaN;
  const fallback = Number(cleaned);
  return Number.isFinite(fallback) ? fallback : NaN;
};

const pickNumber = (...values) => {
  for (const value of values) {
    const n = parseNumeric(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const normalizeItems = (entry = {}) => {
  const direct = Array.isArray(entry?.items)
    ? entry.items
    : Array.isArray(entry?.itemlist)
    ? entry.itemlist
    : Array.isArray(entry?.kot_items)
    ? entry.kot_items
    : null;
  if (direct) return direct;

  const asString =
    typeof entry?.items === "string"
      ? entry.items
      : typeof entry?.itemlist === "string"
      ? entry.itemlist
      : typeof entry?.kot_items === "string"
      ? entry.kot_items
      : "";
  if (!asString) return [];
  try {
    const parsed = JSON.parse(asString);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const lineRate = (item = {}) =>
  pickNumber(
    item?.price,
    item?.rate,
    item?.priceaftertax,
    item?.price_after_tax,
    item?.taxedprice,
    item?.taxed_price,
    item?.unit_price
  );

const lineQty = (item = {}) => pickNumber(item?.qty, item?.quantity, 1);

const lineAmount = (item = {}) => {
  const direct = pickNumber(
    item?.amount,
    item?.total_amount,
    item?.line_total,
    item?.total
  );
  if (direct > 0) return direct;
  const qty = lineQty(item);
  const rate = lineRate(item);
  return Number((qty * rate).toFixed(2));
};

const toDateSafe = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseMinutesValue = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value).trim();
  if (!raw) return 0;
  const num = Number(raw);
  if (!Number.isNaN(num)) return num;
  const match = raw.match(/(\\d+)/);
  return match ? Number(match[1]) || 0 : 0;
};

const diffMinutes = (start, end) => {
  if (!start || !end) return 0;
  const ms = end.getTime() - start.getTime();
  return ms <= 0 ? 0 : Math.round(ms / 60000);
};

const fmtDuration = (mins) => {
  const safe = Math.max(0, Number(mins) || 0);
  if (safe < 60) return `${safe}m`;
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const formatAmount = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "-";
  return fmtINR(n);
};

// ---- table helpers (mirrors mobile logic) ----
const normalizeTableKey = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const isTakeawayKey = (value) => {
  const raw = normalizeTableKey(value).toLowerCase();
  if (!raw) return false;
  return raw === "0" || raw === "-1" || raw.startsWith("takeaway");
};

const getTableKey = (table) =>
  normalizeTableKey(
    table?.table_key ??
      table?.tableKey ??
      table?.key ??
      table?.name ??
      table?.table_no ??
      table?.tableno ??
      table?.table ??
      table?.tableid ??
      table?.tableId ??
      table?.id ??
      ""
  );

const getTableDisplayName = (table) =>
  normalizeTableKey(
    table?.display_name ??
      table?.displayName ??
      table?.tableDisplayName ??
      table?.table_name ??
      table?.name ??
      table?.table ??
      table?.table_no ??
      table?.tableno ??
      table?.table_key ??
      table?.tableKey ??
      ""
  );

const getTableType = (table) => {
  const raw = String(table?.table_type ?? table?.tableType ?? table?.type ?? "table").toLowerCase();
  if (isTakeawayKey(getTableKey(table))) return "takeaway";
  return raw === "room" ? "room" : "table";
};

const buildTableMetaMap = (list = []) => {
  const map = {};
  if (!Array.isArray(list)) return map;
  list.forEach((entry) => {
    const key = getTableKey(entry);
    const numericName = entry?.name;
    const displayName = getTableDisplayName(entry);
    const tableName = normalizeTableKey(entry?.table_name ?? entry?.tableDisplayName ?? "");
    const type = getTableType(entry);
    const resolvedDisplay =
      displayName ||
      (key ? String(key) : numericName !== undefined ? String(numericName) : "");
    const meta = { displayName: resolvedDisplay, type };
    if (key) map[String(key)] = meta;
    if (numericName !== undefined && numericName !== null && numericName !== "") {
      map[String(numericName)] = meta;
    }
    if (tableName) map[String(tableName)] = meta;
    if (resolvedDisplay) map[String(resolvedDisplay)] = meta;
  });
  return map;
};
const resolveTableParts = (value, metaMap) => {
  const raw = normalizeTableKey(value);
  if (!raw) {
    return { prefix: "", displayName: "-", type: "table" };
  }
  if (isTakeawayKey(raw)) {
    const match = raw.toLowerCase().match(/takeaway[-\s]*(\d+)/);
    return {
      prefix: "Takeaway",
      displayName: match ? match[1] : "",
      type: "takeaway",
    };
  }
  const meta = metaMap[raw];
  const displayName = meta?.displayName || raw;
  const type = meta?.type === "room" ? "room" : "table";
  const prefix = type === "room" ? "Room" : "Table";
  return { prefix, displayName, type };
};
const buildPrefixedLabel = (prefix, displayName, separator = " ") => {
  if (!prefix) return displayName || "-";
  const display = String(displayName ?? "").trim();
  if (!display) return prefix;
  const lower = display.toLowerCase();
  const prefixLower = String(prefix).toLowerCase();
  if (lower.startsWith(prefixLower)) return display;
  return `${prefix}${separator}${display}`.trim();
};

const formatTableLabel = (order, tableMetaByKey = {}) => {
  const rawKey =
    order?.table ??
    order?.tableno ??
    order?.table_no ??
    order?.tableid ??
    order?.table_id ??
    order?.tableDisplayName ??
    order?.display_name ??
    order?.table_name ??
    "";
  const base = resolveTableParts(rawKey, tableMetaByKey);
  const fallbackName =
    order?.tableDisplayName ??
    order?.display_name ??
    order?.table_name ??
    null;
  const fallbackTypeRaw =
    order?.tableType ?? order?.table_type ?? order?.type ?? null;
  const fallbackType = String(fallbackTypeRaw || "").trim().toLowerCase();
  const fallbackNameLower = String(fallbackName || "").trim().toLowerCase();
  const displayName = fallbackName || base.displayName || "-";
  const baseType =
    base.type === "takeaway" ? "takeaway" : base.type || "table";
  const type =
    baseType === "takeaway"
      ? "takeaway"
      : fallbackType === "room" || fallbackNameLower.startsWith("room")
      ? "room"
      : baseType;
  const prefix =
    type === "takeaway" ? "Takeaway" : type === "room" ? "Room" : "Table";
  return buildPrefixedLabel(prefix, displayName, " ");
};

const KOTManagementPage = () => {
  const [userId, setUserId] = useState(null);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [tab, setTab] = useState(STATUS.pending);
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, prepared: 0, delivered: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [timingMap, setTimingMap] = useState({});
  const [tableMetaByKey, setTableMetaByKey] = useState({});

  const timingKey = useMemo(
    () => (userId ? `kotTimingMap:${userId}` : null),
    [userId]
  );

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!data?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(data.id);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("tablelistx");
      if (!raw) return;
      const list = JSON.parse(raw);
      if (Array.isArray(list)) setTableMetaByKey(buildTableMetaMap(list));
    } catch {
      setTableMetaByKey({});
    }
  }, []);

  useEffect(() => {
    if (!timingKey) return;
    const raw = localStorage.getItem(timingKey);
    if (!raw) {
      setTimingMap({});
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setTimingMap(parsed && typeof parsed === "object" ? parsed : {});
    } catch {
      setTimingMap({});
    }
  }, [timingKey]);

  useEffect(() => {
    if (!userId) return;
    fetchCounts();
    fetchKOT();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tab, fromDate, toDate]);

  const fetchCounts = async () => {
    try {
      const statuses = [STATUS.pending, STATUS.prepared, STATUS.delivered];
      const resArr = await Promise.all(
        statuses.map((status) =>
          fetch(LIST_KOT_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              restaurantid: userId,
              status,
              fromdate: fromDate,
              todate: toDate,
            }),
          }).then((r) => r.json().catch(() => ({})))
        )
      );
      setCounts({
        pending: resArr[0]?.kot_items?.length || 0,
        prepared: resArr[1]?.kot_items?.length || 0,
        delivered: resArr[2]?.kot_items?.length || 0,
      });
    } catch (e) {
      // ignore counts errors
    }
  };

  const fetchKOT = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetch(LIST_KOT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: userId,
          status: tab,
          fromdate: fromDate,
          todate: toDate,
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false)
        throw new Error(json?.message || "Failed to load KOTs");
      const rows = Array.isArray(json.kot_items) ? json.kot_items : [];

      if (timingKey) {
        const next = { ...(timingMap || {}) };
        let changed = false;
        const nowIso = new Date().toISOString();
        rows.forEach((item) => {
          const id = Number(item.kotitem_id || item.id);
          if (!id) return;
          const prev = next[id] || {};
          const createdMinutes = parseMinutesValue(
            item.created_minutes_ago ?? item.minutes_ago ?? item.created_min_ago
          );
          const fallbackCreated =
            createdMinutes > 0 ? new Date(Date.now() - createdMinutes * 60000).toISOString() : null;
          const createdAtRaw =
            prev.createdAt ||
            item.created_at ||
            item.createdAt ||
            item.start_time ||
            fallbackCreated ||
            nowIso;
          const statusRaw = String(item.status || tab || "").trim().toLowerCase();
          const preparedAtRaw =
            prev.preparedAt ||
            item.prepared_at ||
            (statusRaw === STATUS.prepared || statusRaw === STATUS.delivered
              ? item.updated_at
              : null);
          const deliveredAtRaw =
            prev.deliveredAt ||
            item.delivered_at ||
            (statusRaw === STATUS.delivered ? item.updated_at : null);

          let preparedAt = preparedAtRaw ? new Date(preparedAtRaw) : null;
          let deliveredAt = deliveredAtRaw ? new Date(deliveredAtRaw) : null;

          if ((statusRaw === STATUS.prepared || statusRaw === STATUS.delivered) && !preparedAt) {
            preparedAt = new Date(nowIso);
          }
          if (statusRaw === STATUS.delivered && !deliveredAt) {
            deliveredAt = new Date(nowIso);
          }
          if (deliveredAt && !preparedAt) {
            preparedAt = deliveredAt;
          }

          const nextEntry = {
            createdAt: new Date(createdAtRaw).toISOString(),
            preparedAt: preparedAt ? preparedAt.toISOString() : null,
            deliveredAt: deliveredAt ? deliveredAt.toISOString() : null,
          };

          if (
            prev.createdAt !== nextEntry.createdAt ||
            prev.preparedAt !== nextEntry.preparedAt ||
            prev.deliveredAt !== nextEntry.deliveredAt
          ) {
            next[id] = nextEntry;
            changed = true;
          }
        });
        if (changed) {
          setTimingMap(next);
          localStorage.setItem(timingKey, JSON.stringify(next));
        }
      }

      setList(rows);
    } catch (e) {
      toast.error(e.message || "Unable to load KOTs");
      setList([]);
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const updateStatus = async (itemId, next) => {
    try {
      const res = await fetch(ACTION_KOT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kotitem_id: itemId,
          status: next,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.kotitem)
        throw new Error(json?.message || "Failed to update");
      toast.success(json?.message || "Updated");
      setList((prev) => prev.filter((i) => (i.kotitem_id || i.id) !== itemId));
      if (timingKey) {
        const nowIso = new Date().toISOString();
        setTimingMap((prev) => {
          const nextMap = { ...(prev || {}) };
          const existing = nextMap[itemId] || {};
          const createdAt = existing.createdAt || nowIso;
          if (next === STATUS.prepared) {
            nextMap[itemId] = {
              createdAt,
              preparedAt: nowIso,
              deliveredAt: existing.deliveredAt || null,
            };
          } else if (next === STATUS.delivered) {
            nextMap[itemId] = {
              createdAt,
              preparedAt: existing.preparedAt || nowIso,
              deliveredAt: nowIso,
            };
          }
          localStorage.setItem(timingKey, JSON.stringify(nextMap));
          return nextMap;
        });
      }
      fetchCounts();
    } catch (e) {
      toast.error(e.message || "Unable to update");
    }
  };

  const deleteKotItem = async (itemId) => {
    if (!window.confirm("Delete this KOT item?")) return;
    try {
      const res = await fetch(DELETE_KOT_API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kotitem_id: itemId, restaurantid: userId }) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) throw new Error(json?.message || "Unable to delete KOT item");
      setList((prev) => prev.filter((item) => (item.kotitem_id || item.id) !== itemId));
      toast.success(json?.message || "KOT item deleted");
      fetchCounts();
    } catch (e) { toast.error(e.message); }
  };

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const tabMeta = {
    [STATUS.pending]: { label: "Pending", color: COLOR.amber },
    [STATUS.prepared]: { label: "Prepared", color: COLOR.green },
    [STATUS.delivered]: { label: "Delivered", color: COLOR.primary },
  };

  const totalItems = useMemo(
    () =>
      list.reduce(
        (sum, o) => sum + (Number(o.total_items) || (Array.isArray(o.items) ? o.items.length : 1)),
        0
      ),
    [list]
  );

  if (loading && !list.length) {
    return (
      <Center>
        <div className="spinner" />
        <div>Loading KOTs…</div>
      </Center>
    );
  }

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <TopBar>
        <div>
          <Title>KOT Management</Title>
          <Sub>Track kitchen tickets by status.</Sub>
          <PillRow>
            <Pill>Total: {list.length}</Pill>
            <Pill $muted>Items: {totalItems}</Pill>
          </PillRow>
        </div>
        <Actions>
          <DateInput>
            From:
            <input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} />
          </DateInput>
          <DateInput>
            To:
            <input type="date" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} />
          </DateInput>
          <PrimaryBtn onClick={() => fetchKOT({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </Actions>
      </TopBar>

      <Tabs>
        {Object.keys(tabMeta).map((key) => {
          const active = tab === key;
          const meta = tabMeta[key];
          const count =
            key === STATUS.pending
              ? counts.pending
              : key === STATUS.prepared
              ? counts.prepared
              : counts.delivered;
          return (
            <TabBtn key={key} $active={active} onClick={() => setTab(key)}>
              <Dot style={{ background: meta.color }} />
              <span>
                {meta.label} ({count})
              </span>
            </TabBtn>
          );
        })}
      </Tabs>

      {loading ? (
        <Center>Loading…</Center>
      ) : list.length === 0 ? (
        <Center>No KOTs for this range/status.</Center>
      ) : (
        <List>
          {list.map((o) => {
            const isExpanded = !!expanded[o.kotitem_id || o.id];
            const status = tabMeta[tab];
            const isPending = tab === STATUS.pending;
            const isPrepared = tab === STATUS.prepared;
            const items = normalizeItems(o);
            const displayItems =
              items.length > 0
                ? items
              : o.product_name
              ? [
                  {
                    product_name: o.product_name,
                    qty: o.qty,
                    rate: lineRate(o),
                  },
                ]
              : [];
            const totalQty =
              displayItems.length > 0
                ? displayItems.reduce((s, it) => s + lineQty(it), 0)
                : Number(o.qty || 0);
            const derivedAmount =
              displayItems.length > 0
                ? displayItems.reduce((sum, it) => sum + lineAmount(it), 0)
                : lineAmount(o);
            const cardAmount = pickNumber(
              o.total_amount,
              o.amount,
              o.bill_amount,
              o.grand_total,
              o.totalamount,
              o.totalAmount,
              derivedAmount
            );
            const createdMinutes = parseMinutesValue(
              o.created_minutes_ago ?? o.minutes_ago ?? o.created_min_ago
            );
            const createdAtFallback =
              createdMinutes > 0 ? new Date(Date.now() - createdMinutes * 60000) : null;
            const timingEntry = timingMap?.[Number(o.kotitem_id || o.id)];
            const createdAt =
              toDateSafe(timingEntry?.createdAt) ||
              toDateSafe(o.created_at) ||
              toDateSafe(o.createdAt) ||
              toDateSafe(o.start_time) ||
              createdAtFallback ||
              new Date();
            const preparedAt =
              toDateSafe(timingEntry?.preparedAt) ||
              toDateSafe(o.prepared_at) ||
              (tab !== STATUS.pending ? toDateSafe(o.updated_at) : null);
            const deliveredAt =
              toDateSafe(timingEntry?.deliveredAt) ||
              toDateSafe(o.delivered_at) ||
              (tab === STATUS.delivered ? toDateSafe(o.updated_at) : null);
            const now = new Date();
            const pendingMin = preparedAt
              ? diffMinutes(createdAt, preparedAt)
              : createdMinutes > 0
              ? createdMinutes
              : diffMinutes(createdAt, now);
            const deliveryMin = preparedAt && deliveredAt ? diffMinutes(preparedAt, deliveredAt) : 0;
            const totalMin = deliveredAt ? diffMinutes(createdAt, deliveredAt) : pendingMin;
            const waitingMin = preparedAt && !deliveredAt ? diffMinutes(preparedAt, now) : 0;

            const statusTimeText = isPending
              ? `Pending time: ${fmtDuration(pendingMin)}`
              : isPrepared
              ? `Prep time: ${fmtDuration(pendingMin)}`
              : `Total time: ${fmtDuration(totalMin)}`;

            const timingDetail = isPending
              ? ""
              : isPrepared
              ? `Pending time: ${fmtDuration(pendingMin)} • Waiting delivery: ${fmtDuration(waitingMin)}`
              : `Pending time: ${fmtDuration(pendingMin)} • Delivery time: ${fmtDuration(deliveryMin)} • Total time: ${fmtDuration(totalMin)}`;
            return (
              <Card key={o.kotitem_id || o.id}>
                <CardHead>
                  <div>
                    <CardTitle>{formatTableLabel(o, tableMetaByKey)}</CardTitle>
                    <CardMeta>
                      Order #{o.order_id || o.orderid || o.kotid || o.id} ·{" "}
                      {createdAt.toLocaleString("en-IN")}
                    </CardMeta>
                  </div>
                  <StatusChip style={{ background: status.color + "22", color: status.color }}>
                    {status.label}
                  </StatusChip>
                </CardHead>
                <CardMetaRow>
                  <MetaPill>Total Items: {displayItems.length}</MetaPill>
                  <MetaPill>Total Qty: {totalQty}</MetaPill>
                  <MetaPill>Amount: {formatAmount(cardAmount)}</MetaPill>
                  <MetaPill>{statusTimeText}</MetaPill>
                </CardMetaRow>
                {timingDetail ? <TimingNote>{timingDetail}</TimingNote> : null}
                <Divider />
                <ExpandRow onClick={() => toggleExpand(o.kotitem_id || o.id)}>
                  <span>{isExpanded ? "Hide items" : "View items"}</span>
                  {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </ExpandRow>
                {isExpanded && displayItems.length > 0 && (
                  <Items>
                    {displayItems.map((it, idx) => (
                      <ItemRow key={idx}>
                        <ItemName>{it.name || it.product_name}</ItemName>
                        <ItemQty>{lineQty(it)}</ItemQty>
                        <ItemAmt>{formatAmount(lineAmount(it))}</ItemAmt>
                      </ItemRow>
                    ))}
                  </Items>
                )}
                <ActionRow>
                  <DangerBtn onClick={() => deleteKotItem(o.kotitem_id || o.id)}>Delete</DangerBtn>
                  {tab === STATUS.pending && (
                    <>
                      <PrimaryBtn small onClick={() => updateStatus(o.kotitem_id || o.id, STATUS.prepared)}>
                        <FaCheckCircle /> Prepared
                      </PrimaryBtn>
                      <GhostBtn onClick={() => updateStatus(o.kotitem_id || o.id, STATUS.delivered)}>
                        <FaBox /> Delivered
                      </GhostBtn>
                    </>
                  )}
                  {tab === STATUS.prepared && (
                    <PrimaryBtn small onClick={() => updateStatus(o.kotitem_id || o.id, STATUS.delivered)}>
                      <FaBox /> Mark Delivered
                    </PrimaryBtn>
                  )}
                </ActionRow>
              </Card>
            );
          })}
        </List>
      )}
    </Page>
  );
};

export default KOTManagementPage;

const today = () => {
  const d = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* ---------------- styled ---------------- */
const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;
const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;
const Sub = styled.div`
  color: ${COLOR.muted};
`;
const PillRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;
const Pill = styled.span`
  background: ${({ $muted }) => ($muted ? "#eef2ff" : "#dcfce7")};
  color: ${({ $muted }) => ($muted ? COLOR.text : "#166534")};
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ small }) => (small ? "8px 10px" : "10px 12px")};
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
`;
const GhostBtn = styled(PrimaryBtn)`
  background: #eef6ff;
  color: ${COLOR.primary};
  border: 1px solid ${COLOR.primary};
`;
const DangerBtn = styled(PrimaryBtn)`background:#fff1f2;color:#be123c;border:1px solid #fecdd3;`;
const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
`;
const TabBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${COLOR.line};
  background: ${({ $active }) => ($active ? "#fff" : "#f3f6fb")};
  color: ${COLOR.text};
  font-weight: 800;
  cursor: pointer;
  box-shadow: ${({ $active }) =>
    $active ? "0 8px 18px rgba(0,0,0,0.08)" : "none"};
`;
const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 10px;
  align-items: stretch;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
`;
const CardTitle = styled.div`
  font-weight: 900;
  color: ${COLOR.text};
  font-size: 1rem;
`;
const CardMeta = styled.div`
  color: ${COLOR.muted};
  font-size: 13px;
`;
const StatusChip = styled.span`
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
`;
const CardMetaRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
  color: ${COLOR.muted};
  font-size: 13px;
`;
const TimingNote = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${COLOR.muted};
`;
const MetaPill = styled.span`
  background: #f3f6fb;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid ${COLOR.line};
`;
const Divider = styled.div`
  height: 1px;
  background: ${COLOR.line};
  margin: 10px 0;
`;
const ExpandRow = styled.button`
  border: none;
  background: #f9fafb;
  border-radius: 10px;
  padding: 8px 10px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: ${COLOR.text};
  font-weight: 700;
`;
const Items = styled.div`
  margin-top: 8px;
  display: grid;
  gap: 6px;
`;
const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f8fafc;
`;
const ItemName = styled.div`
  font-weight: 700;
  color: ${COLOR.text};
  min-width: 0;
`;
const ItemQty = styled.div`
  color: ${COLOR.muted};
  font-weight: 700;
`;
const ItemAmt = styled.div`
  text-align: right;
  font-weight: 800;
  color: ${COLOR.text};
`;
const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
  padding-top: 10px;
  flex-wrap: wrap;
`;

const Center = styled.div`
  display: grid;
  place-items: center;
  min-height: 30vh;
  color: ${COLOR.muted};
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: ${COLOR.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
